import { CircleAlert, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth/auth-context";
import { isGroupMember } from "@/lib/auth/roles";
import type { ApiError } from "@/lib/api/unwrap";

import {
  useCancelSubscription,
  useCreateSubscription,
  useStripeProducts,
  useSubscriptionStatus,
  useValidateCode,
  type StripeProduct,
  type SubscriptionStatus,
} from "./api";
import {
  applyDiscount,
  formatCents,
  intervalLabel,
  readPrice,
  type Coupon,
} from "./pricing";

/** The status payload is a full object only when a Stripe subscription exists. */
function hasActiveSubscription(
  status: SubscriptionStatus | undefined,
): status is SubscriptionStatus {
  return Boolean(status && status.name && status.cost != null);
}

function ErrorLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
      <CircleAlert className="size-4" aria-hidden />
      {children}
    </p>
  );
}

/** A titled card wrapping a single status/loading/error message. */
function SummaryMessage({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("billing.summary.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={tone === "error" ? "text-destructive" : "text-muted-foreground"}>
          {children}
        </p>
      </CardContent>
    </Card>
  );
}

/** Read-only summary of the active subscription, with a cancel action. */
function ActiveSubscription({ status }: { status: SubscriptionStatus }) {
  const { t, i18n } = useTranslation();
  const cancel = useCancelSubscription();
  const currency = status.currency ?? "cad";
  const finalCents = status.final_cost != null ? Number(status.final_cost) : null;
  const hasDiscount = finalCents != null && finalCents !== status.cost;
  const discountName =
    status.discount &&
    typeof status.discount === "object" &&
    "coupon" in status.discount
      ? ((status.discount.coupon as { name?: string } | null)?.name ?? null)
      : null;

  // interval / card are untyped (DictField / method fields) — read defensively.
  const interval = status.interval ?? null;
  const intervalCount = status.interval_count ?? 1;
  const intervalText = interval
    ? intervalCount > 1
      ? `${intervalCount} ${t(`billing.interval.${interval}`, { defaultValue: interval })}`
      : t(`billing.interval.${interval}`, { defaultValue: interval })
    : null;
  const card = status.card as
    | { brand?: string; last4?: string; exp_month?: number; exp_year?: number }
    | undefined;
  const hasCard = Boolean(card && card.brand && card.last4);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("billing.summary.currentPlan")}</CardTitle>
        <CardDescription>{status.name}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("billing.summary.status")}</dt>
            <dd className="font-medium">
              {t(`billing.status.${status.status}`, { defaultValue: status.status })}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("billing.summary.price")}</dt>
            <dd className={hasDiscount ? "text-muted-foreground line-through" : "font-medium"}>
              {formatCents(status.cost, currency, i18n.language)}
            </dd>
          </div>
          {hasDiscount && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                {discountName ?? t("billing.summary.discount")}
              </dt>
              <dd className="font-semibold text-success">
                {formatCents(finalCents, currency, i18n.language)}
              </dd>
            </div>
          )}
          {intervalText && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                {t("billing.summary.billingInterval")}
              </dt>
              <dd>{intervalText}</dd>
            </div>
          )}
          {status.current_period_start && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                {t("billing.summary.startedOn")}
              </dt>
              <dd className="tabular-nums">{status.current_period_start}</dd>
            </div>
          )}
          {status.current_period_end && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                {t("billing.summary.renewsOn")}
              </dt>
              <dd className="tabular-nums">{status.current_period_end}</dd>
            </div>
          )}
        </dl>

        {hasCard && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("billing.summary.cardOnFile")}
            </p>
            <div className="flex items-center gap-3 rounded-md border p-3">
              <span className="grid h-6 w-9 place-items-center rounded bg-muted text-[10px] font-bold uppercase text-muted-foreground">
                {card?.brand}
              </span>
              <div className="text-sm">
                <div className="font-medium">•••• {card?.last4}</div>
                {card?.exp_month && card?.exp_year && (
                  <div className="text-xs text-muted-foreground">
                    {t("billing.summary.cardExpires", {
                      month: String(card.exp_month).padStart(2, "0"),
                      year: card.exp_year,
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex-col items-start gap-2">
        {cancel.isError && (
          <ErrorLine>
            {(cancel.error as unknown as ApiError).detail ??
              t("billing.summary.cancelError")}
          </ErrorLine>
        )}
        <Button
          type="button"
          variant="destructive"
          disabled={cancel.isPending}
          onClick={() => {
            if (window.confirm(t("billing.summary.cancelConfirm"))) cancel.mutate();
          }}
        >
          {cancel.isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
          {t("billing.summary.cancel")}
        </Button>
      </CardFooter>
    </Card>
  );
}

/** Order summary + confirm for users without a subscription. */
function ChoosePlan({ products }: { products: StripeProduct[] }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  // Someone invited by email is already a member: their group's discount applies
  // automatically and there is nothing for them to type. Everyone else gets one
  // field that accepts a partner code, a group-bound promo code, or a plain
  // Stripe promotion code.
  const groupMember = isGroupMember(user);
  const multiPlan = products.length > 1;

  const [selectedId, setSelectedId] = useState<string>(products[0]?.id ?? "");
  const [codeInput, setCodeInput] = useState("");
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);

  const validateCode = useValidateCode();
  const createSubscription = useCreateSubscription();

  const selected = products.find((p) => p.id === selectedId) ?? products[0];
  const price = selected ? readPrice(selected) : null;
  const baseCents = price?.unitAmount ?? null;
  // A validated code — partner or promo — previews its discount here. An
  // already-invited member's group discount is still applied server-side.
  const totalCents =
    !groupMember && baseCents != null ? applyDiscount(baseCents, coupon) : baseCents;
  const currency = price?.currency ?? "cad";
  const discounted = !groupMember && totalCents != null && totalCents !== baseCents;

  function onValidateCode() {
    if (!codeInput.trim()) return;
    validateCode.mutate(codeInput.trim(), {
      onSuccess: (res) => {
        setCoupon(res.coupon);
        setGroupName(res.group_name ?? null);
      },
    });
  }

  function onConfirm() {
    if (!price) return;
    createSubscription.mutate({
      price_id: price.id,
      // Send whatever was typed, not only a code that was "Applied" first.
      // Gating on `coupon` silently dropped the code of anyone who typed it
      // and hit Confirm directly — no group, no discount, and no error to
      // tell them. An unvalidated typo now fails loudly at confirm instead.
      code: groupMember ? "" : codeInput.trim(),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("billing.summary.orderSummary")}</CardTitle>
        <CardDescription>{t("billing.summary.choosePlanHint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {multiPlan ? (
          // More than one Stripe plan → a dropdown (matches the legacy select).
          <div className="space-y-1.5">
            <Label htmlFor="plan-select">{t("billing.summary.subscriptionType")}</Label>
            <select
              id="plan-select"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="flex min-h-touch w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {products.map((product) => {
                const p = readPrice(product);
                return (
                  <option key={product.id} value={product.id}>
                    {product.name} —{" "}
                    {formatCents(p.unitAmount, p.currency, i18n.language)}{" "}
                    {intervalLabel(p, t)}
                  </option>
                );
              })}
            </select>
          </div>
        ) : (
          // Single plan → shown fixed, no picker.
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-medium">{selected?.name}</span>
            <span className="text-sm text-muted-foreground">
              {formatCents(baseCents, currency, i18n.language)} {price ? intervalLabel(price, t) : ""}
            </span>
          </div>
        )}

        {groupMember ? (
          <div className="rounded-md border border-theme-economic/30 bg-theme-economic-soft p-3 text-sm text-theme-economic-ink">
            {t("billing.summary.groupDiscountBanner")}{" "}
            {t("billing.summary.groupDiscountNote")}
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="access-code">{t("billing.summary.codeLabel")}</Label>
            <div className="flex gap-2">
              <Input
                id="access-code"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setCoupon(null);
                  setGroupName(null);
                }}
                placeholder={t("billing.summary.codePlaceholder")}
              />
              <Button
                type="button"
                variant="outline"
                onClick={onValidateCode}
                disabled={validateCode.isPending || !codeInput.trim()}
              >
                {validateCode.isPending && (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                )}
                {t("billing.summary.applyCode")}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("billing.summary.codeHint")}
            </p>
            {coupon && (
              <p className="text-sm font-medium text-success">
                {groupName
                  ? t("billing.summary.codeAppliedGroup", { group: groupName })
                  : t("billing.summary.promoApplied", {
                      name: coupon.name || t("billing.summary.discount"),
                    })}
              </p>
            )}
            {validateCode.isError && (
              <ErrorLine>
                {(validateCode.error as unknown as ApiError).detail ??
                  t("billing.summary.codeInvalid")}
              </ErrorLine>
            )}
          </div>
        )}

        <dl className="space-y-1 border-t pt-4 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">{t("billing.summary.price")}</dt>
            <dd className={discounted ? "text-muted-foreground line-through" : ""}>
              {formatCents(baseCents, currency, i18n.language)}
            </dd>
          </div>
          {discounted && (
            <div className="flex justify-between">
              <dt className="text-muted-foreground">
                {t("billing.summary.discount")}
              </dt>
              <dd className="text-success">
                −{formatCents((baseCents ?? 0) - (totalCents ?? 0), currency, i18n.language)}
              </dd>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold">
            <dt>{t("billing.summary.total")}</dt>
            <dd>{formatCents(totalCents, currency, i18n.language)}</dd>
          </div>
        </dl>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2">
        {createSubscription.isError && (
          <ErrorLine>
            {(createSubscription.error as unknown as ApiError).detail ??
              t("billing.summary.confirmError")}
          </ErrorLine>
        )}
        <Button
          type="button"
          onClick={onConfirm}
          disabled={!price || createSubscription.isPending}
        >
          {createSubscription.isPending && (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          )}
          {t("billing.summary.confirm")}
        </Button>
      </CardFooter>
    </Card>
  );
}

export function SubscriptionSummary() {
  const { t } = useTranslation();
  const status = useSubscriptionStatus();
  const products = useStripeProducts();

  if (status.isPending) {
    return <SummaryMessage>{t("billing.loading")}</SummaryMessage>;
  }

  // A failed status fetch must NOT fall through to the plan picker — that would
  // show "choose a plan" to a user who may already have an active subscription.
  if (status.isError) {
    return <SummaryMessage tone="error">{t("billing.loadError")}</SummaryMessage>;
  }

  if (hasActiveSubscription(status.data)) {
    return <ActiveSubscription status={status.data} />;
  }

  if (products.isPending) {
    return <SummaryMessage>{t("billing.loading")}</SummaryMessage>;
  }

  if (products.isError) {
    return <SummaryMessage tone="error">{t("billing.loadError")}</SummaryMessage>;
  }

  if (!products.data || products.data.length === 0) {
    return <SummaryMessage>{t("billing.summary.noProducts")}</SummaryMessage>;
  }

  return <ChoosePlan products={products.data} />;
}
